import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { ScrollReveal } from '../../hooks/useScrollAnimation';
import { 
  Zap, Battery, MapPin, Smartphone, CreditCard, Clock, 
  ChevronRight, Building2, Users, Leaf, ArrowRight,
  Shield, Globe, Gauge, Download, QrCode, Wallet, Plug,
  Server, Check, BarChart3, Truck, Store, Hotel, TreePine,
  ChevronDown
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePageEN = () => {
  const [stats, setStats] = useState(null);
  
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats`);
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };
    fetchStats();
  }, []);

  const features = [
    {
      icon: Zap,
      title: 'Ultra-Fast Charging',
      description: 'Over 240 kW charging power. Charge your electric vehicle in just 15-20 minutes.',
      color: 'bg-lime-100 text-lime-600'
    },
    {
      icon: MapPin,
      title: 'Wide Network',
      description: 'Charging stations across Albania, in major cities and main roads.',
      color: 'bg-blue-100 text-blue-600'
    },
    {
      icon: Smartphone,
      title: 'Mobile App',
      description: 'Find stations, start charging, and pay directly from the app.',
      color: 'bg-purple-100 text-purple-600'
    },
    {
      icon: CreditCard,
      title: 'Easy Payment',
      description: 'Pay by card, app, or RFID. Transparent invoices for every session.',
      color: 'bg-rose-100 text-rose-600'
    },
    {
      icon: Building2,
      title: 'Business Solutions',
      description: 'Special packages for companies and electric fleet management.',
      color: 'bg-amber-100 text-amber-600'
    },
    {
      icon: Leaf,
      title: 'Clean Energy',
      description: 'Contribute to a greener Albania with 100% renewable energy.',
      color: 'bg-emerald-100 text-emerald-600'
    }
  ];

  return (
    <div data-testid="home-page-en" className="min-h-screen">
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-screen flex items-center overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)' }}
      >
        <div 
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-lime-400 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-400 rounded-full blur-3xl" />
        </div>
        
        <div className="container-custom relative z-10 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-white/20">
                <span className="w-2 h-2 rounded-full bg-lime-400 animate-pulse" />
                <span className="text-sm font-medium">Fast and Secure Charging for Your Electric Vehicle</span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-bold font-['Inter_Tight'] tracking-tight mb-6 leading-tight">
                Energy for
                <span className="text-lime-400 block">Mobility</span>
              </h1>
              
              <p className="text-xl text-blue-100 mb-8 leading-relaxed max-w-lg">
                AlbMove offers the widest network of charging stations for electric vehicles in Albania. Fast, safe, and affordable charging.
              </p>
              
              <div className="flex flex-wrap gap-3">
                <a 
                  href="https://apps.apple.com/hr/app/albmove/id6759310357"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-app-store-link"
                  className="inline-flex items-center gap-2 bg-white text-slate-900 rounded-lg px-4 py-2 font-medium hover:bg-lime-100 transition-all hover:scale-105"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <div className="text-left">
                    <p className="text-[10px] opacity-70 leading-tight">Download on</p>
                    <p className="text-sm font-semibold leading-tight">App Store</p>
                  </div>
                </a>
                <a 
                  href="https://play.google.com/store/apps/details?id=com.albmove.client"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-play-store-link"
                  className="inline-flex items-center gap-2 bg-white text-slate-900 rounded-lg px-4 py-2 font-medium hover:bg-lime-100 transition-all hover:scale-105"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                  </svg>
                  <div className="text-left">
                    <p className="text-[10px] opacity-70 leading-tight">Get it on</p>
                    <p className="text-sm font-semibold leading-tight">Google Play</p>
                  </div>
                </a>
              </div>
            </div>
            
            <div className="relative hidden lg:block">
              <div className="relative">
                <video 
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                  className="rounded-3xl shadow-2xl w-full h-auto object-cover"
                  data-testid="hero-video"
                  poster="https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=800"
                >
                  <source src="https://videos.pexels.com/video-files/5765104/5765104-uhd_2560_1440_25fps.mp4" type="video/mp4" />
                  Your browser does not support video.
                </video>
                <div className="absolute -bottom-4 -left-4 bg-white/90 backdrop-blur rounded-2xl p-4 shadow-lg border border-white/50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-lime-100 flex items-center justify-center">
                      <Zap className="w-6 h-6 text-lime-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900 font-['Inter_Tight']">8+</p>
                      <p className="text-sm text-slate-500">Active Stations</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 rounded-full border-2 border-white/30 flex items-start justify-center pt-2">
            <div className="w-1 h-3 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>
      
      {/* Stats Section */}
      <section className="py-16 bg-white border-b border-slate-100">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: MapPin, value: stats?.total_stations || '8', suffix: '+', label: 'Stations' },
              { icon: Battery, value: stats?.total_chargers || '25', suffix: '+', label: 'Chargers' },
              { icon: Building2, value: stats?.cities_covered || '8', suffix: '', label: 'Cities' },
              { icon: Zap, value: stats?.total_energy_delivered || '1250', suffix: '+', label: 'MWh Delivered' }
            ].map((stat, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 100}>
                <div className="flex items-center justify-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-lime-100 flex items-center justify-center flex-shrink-0">
                    <stat.icon className="w-6 h-6 text-lime-500" />
                  </div>
                  <div>
                    <p className="text-3xl md:text-4xl font-bold text-blue-600 font-['Inter_Tight']">
                      {stat.value}{stat.suffix}
                    </p>
                    <p className="text-slate-500 text-sm">{stat.label}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <ScrollReveal animation="fade-up">
            <div className="text-center mb-16">
              <span className="inline-block text-sm font-medium uppercase tracking-widest text-blue-600 mb-4">
                Why AlbMove?
              </span>
              <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 font-['Inter_Tight'] tracking-tight">
                Complete Charging Experience
              </h2>
            </div>
          </ScrollReveal>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 100}>
                <div className="feature-card group h-full">
                  <div className={`w-14 h-14 rounded-2xl ${feature.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                    <feature.icon className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 font-['Inter_Tight'] mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-slate-500 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      
      {/* How It Works Section */}
      <section className="section-padding" style={{ backgroundColor: '#1a3a2f' }}>
        <div className="container-custom">
          <ScrollReveal animation="fade-up">
            <div className="text-center mb-20">
              <h2 className="text-4xl md:text-5xl font-bold text-white font-['Inter_Tight'] tracking-tight mb-4">
                How It Works?
              </h2>
              <p className="text-lg text-gray-400">
                Simple charging process with AlbMove
              </p>
            </div>
          </ScrollReveal>
          
          <div className="relative">
            <div className="hidden lg:block absolute top-[140px] left-[12%] right-[12%] h-0.5 bg-lime-400/40" />
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
              {[
                { step: '01', icon: Download, title: 'Download App', desc: 'Install AlbMove app from App Store or Google Play' },
                { step: '02', icon: MapPin, title: 'Find Station', desc: 'Use the map to find the nearest station' },
                { step: '03', icon: QrCode, title: 'Scan & Charge', desc: 'Scan the QR code and start charging' },
                { step: '04', icon: Wallet, title: 'Pay', desc: 'Automatic payment from your digital wallet' }
              ].map((item, index) => (
                <ScrollReveal key={index} animation="fade-up" delay={index * 150}>
                  <div className="relative text-center group">
                    <div className="text-7xl md:text-8xl font-bold text-lime-400/15 font-['Inter_Tight'] leading-none select-none">
                      {item.step}
                    </div>
                    <div className="relative -mt-8 mb-8">
                      {index > 0 && (
                        <div className="hidden lg:block absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full">
                          <ChevronRight className="w-6 h-6 text-lime-400/60 animate-pulse" />
                        </div>
                      )}
                      <div className="w-20 h-28 rounded-full bg-lime-400 flex items-center justify-center mx-auto shadow-lg shadow-lime-400/20 group-hover:scale-110 group-hover:shadow-lime-400/40 transition-all duration-300 cursor-pointer">
                        <item.icon className="w-9 h-9 text-[#2568b0]" strokeWidth={1.5} />
                      </div>
                    </div>
                    <h3 className="text-xl font-semibold text-white font-['Inter_Tight'] mb-3 group-hover:text-lime-400 transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed max-w-[220px] mx-auto">
                      {item.desc}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* Charger Types Section */}
      <section className="section-padding" style={{ backgroundColor: '#e8f4f8' }}>
        <div className="container-custom">
          <ScrollReveal animation="fade-up">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 font-['Inter_Tight'] tracking-tight mb-4">
                Charger Types
              </h2>
              <p className="text-lg text-slate-500">
                AC and DC for every need
              </p>
            </div>
          </ScrollReveal>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <ScrollReveal animation="fade-right">
              <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100 h-full">
                <div className="flex items-start gap-5 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Plug className="w-7 h-7 text-[#2568b0]" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mb-2">
                      AC Chargers
                    </h3>
                    <p className="text-slate-500 leading-relaxed">
                      Ideal for slow charging overnight or at work. Power: 3.7kW - 22kW. Charging time: 4-8 hours.
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <div className="inline-flex items-center gap-2 bg-slate-100 rounded-full px-4 py-2">
                    <Battery className="w-4 h-4 text-slate-600" />
                    <span className="text-sm font-medium text-slate-700">Power: 3.7kW - 22kW</span>
                  </div>
                  <div className="inline-flex items-center gap-2 bg-slate-100 rounded-full px-4 py-2">
                    <Clock className="w-4 h-4 text-slate-600" />
                    <span className="text-sm font-medium text-slate-700">Time: 4-8 hours</span>
                  </div>
                </div>
              </div>
            </ScrollReveal>
            
            <ScrollReveal animation="fade-left" delay={150}>
              <div className="bg-[#2568b0] rounded-3xl p-8 shadow-lg h-full">
                <div className="flex items-start gap-5 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-7 h-7 text-lime-400" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white font-['Inter_Tight'] mb-2">
                      DC Chargers (Fast Charging)
                    </h3>
                    <p className="text-blue-100 leading-relaxed">
                      For fast charging on the go. Power: 80kW - 240kW. Charging time: 15-30 minutes.
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <div className="inline-flex items-center gap-2 bg-lime-400 rounded-full px-4 py-2">
                    <Battery className="w-4 h-4 text-slate-900" />
                    <span className="text-sm font-semibold text-slate-900">Power: 80kW - 240kW</span>
                  </div>
                  <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2">
                    <Clock className="w-4 h-4 text-white" />
                    <span className="text-sm font-medium text-white">Time: 15-30 min</span>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
      
      {/* EV Infrastructure Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <ScrollReveal animation="fade-up">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 font-['Inter_Tight'] tracking-tight mb-4">
                EV Infrastructure
              </h2>
              <p className="text-lg text-slate-500">
                Charging solutions for every business
              </p>
            </div>
          </ScrollReveal>
          
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                num: '01', icon: Truck, title: 'Fleet Management', subtitle: 'Optimize operations',
                description: 'Centralized charging for your EV fleet. Reduce operating costs and maximize efficiency with real-time monitoring.',
                link: '/en/business/fleet'
              },
              {
                num: '02', icon: Store, title: 'Venue Charging', subtitle: 'Attract more customers',
                description: 'Offer EV chargers at your restaurant, mall, or business. Modern customers choose places that offer charging.',
                link: '/en/business/venue'
              },
              {
                num: '03', icon: Hotel, title: 'Hotels & Tourism', subtitle: 'Premium service',
                description: 'EV charging as a premium amenity for your guests. Attract eco-conscious travelers and create additional revenue.',
                link: '/en/business/hospitality'
              },
              {
                num: '04', icon: TreePine, title: 'Public Infrastructure', subtitle: 'Sustainable mobility',
                description: 'Install chargers in public spaces and support the transition to clean transport. Ideal for municipalities and organizations.',
                link: '/en/business/public'
              }
            ].map((item, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 100}>
                <Link to={item.link} className="group block bg-slate-50 rounded-3xl p-8 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-start gap-4 mb-4">
                    <span className="text-5xl font-bold text-blue-600/20 font-['Inter_Tight'] leading-none">{item.num}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                          <item.icon className="w-5 h-5 text-blue-600 group-hover:text-white transition-colors" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 font-['Inter_Tight'] group-hover:text-blue-600 transition-colors">{item.title}</h3>
                      </div>
                      <span className="inline-block text-sm font-medium text-lime-600 mb-3">{item.subtitle}</span>
                      <p className="text-slate-600 text-sm leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-blue-600 font-medium text-sm mt-4">
                    <span>Learn more</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="section-padding" style={{ backgroundColor: '#e8f4f0' }}>
        <div className="container-custom">
          <ScrollReveal animation="fade-up">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 font-['Inter_Tight'] tracking-tight mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-slate-500">
                Answers to your questions
              </p>
            </div>
          </ScrollReveal>
          
          <div className="max-w-3xl mx-auto space-y-4">
            {[
              { question: 'How do I start using AlbMove?', answer: 'Download the AlbMove app from App Store or Google Play, create an account, top up your wallet, and you\'re ready to charge. Find the nearest station on the map and scan the QR code to start.' },
              { question: 'What payment methods do you accept?', answer: 'We accept credit/debit cards, bank transfers, and cash at authorized points. Our digital wallet system works with credits where 1 Credit = 1 Lek.' },
              { question: 'Can I use your chargers with any electric vehicle?', answer: 'Yes, our stations are compatible with all electric vehicles that use CCS, GB/T, and Type 2 standards. Check the app to see the connector type available at each station.' },
              { question: 'How does dynamic pricing work?', answer: 'Dynamic pricing adjusts according to the time of day and demand. Prices are lower during off-peak hours and higher during peak hours. The current price is always displayed in the app before starting charging.' },
              { question: 'Are you fiscally registered?', answer: 'Yes, AlbMove is fully fiscally registered according to Albanian legislation. Every charging session automatically generates a fiscal invoice that is sent to your email.' }
            ].map((faq, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 50}>
                <details className="group bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                  <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                    <span className="font-semibold text-slate-900 pr-4">{faq.question}</span>
                    <ChevronDown className="w-5 h-5 text-slate-400 group-open:rotate-180 transition-transform flex-shrink-0" />
                  </summary>
                  <div className="px-6 pb-6 pt-0 text-slate-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <ScrollReveal animation="zoom-in">
            <div className="rounded-3xl p-12 md:p-16 text-center relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)' }}>
              <div className="absolute top-0 right-0 w-64 h-64 bg-lime-400/10 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-400/10 rounded-full blur-3xl" />
              
              <div className="relative z-10">
                <h2 className="text-3xl md:text-5xl font-bold text-white font-['Inter_Tight'] mb-6">
                  Ready to Get Started?
                </h2>
                <p className="text-lg text-blue-100 mb-10 max-w-xl mx-auto">
                  Join thousands of users who have chosen AlbMove for charging their electric vehicles.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    asChild
                    className="bg-lime-400 hover:bg-lime-300 text-slate-900 rounded-full px-8 py-6 text-lg font-semibold"
                  >
                    <Link to="/en/contact">
                      Contact Us
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Link>
                  </Button>
                  <Button 
                    asChild
                    variant="outline"
                    className="bg-transparent border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-medium"
                  >
                    <Link to="/en/stations">
                      View Stations
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};

export default HomePageEN;
